import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { Reveal } from "../components/shared";
import { InquiryForm } from "../components/InquiryForm";
import { Button } from "../components/ui/button";
import { Phone, Mail, MapPin, Clock, CalendarCheck, GraduationCap, MessageSquare } from "lucide-react";

export default function Contact() {
  const { t } = useI18n();
  const [site, setSite] = useState({});
  useEffect(() => { api.get("/site").then(({ data }) => setSite(data)).catch(() => {}); }, []);

  const actions = [
    { type: "tour", icon: CalendarCheck, title: t.form.tour, desc: "Visit us and see Ainalem in action." },
    { type: "enrollment", icon: GraduationCap, title: t.form.enroll, desc: "Start the enrollment or waitlist process." },
    { type: "contact", icon: MessageSquare, title: t.form.contact, desc: "Have a question? Send us a note." },
  ];

  return (
    <div className="pt-28">
      <section className="max-w-7xl mx-auto px-6 py-12">
        <Reveal>
          <div className="text-coral font-semibold tracking-widest uppercase text-xs mb-3">Get in touch</div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-navy tracking-tight">Let's connect</h1>
          <p className="mt-5 text-lg text-navy/60 max-w-2xl">We'd love to welcome your family. Book a tour, ask a question, or start your enrollment — we respond within one business day.</p>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-12 grid lg:grid-cols-3 gap-6">
        {actions.map((a, i) => (
          <Reveal key={a.type} delay={i * 0.1}>
            <div className="bg-white rounded-3xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.06)] h-full flex flex-col" data-testid={`contact-action-${a.type}`}>
              <div className="h-12 w-12 rounded-2xl bg-coral/10 flex items-center justify-center mb-4"><a.icon className="h-6 w-6 text-coral" /></div>
              <h3 className="font-heading text-xl font-bold text-navy">{a.title}</h3>
              <p className="text-navy/60 mt-2 flex-1">{a.desc}</p>
              <InquiryForm type={a.type} trigger={<Button className="mt-5 rounded-full bg-navy hover:bg-navy-dark text-white w-full" data-testid={`contact-open-${a.type}`}>{a.title}</Button>} />
            </div>
          </Reveal>
        ))}
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-16 grid lg:grid-cols-2 gap-8">
        <Reveal>
          <div className="bg-navy text-white rounded-3xl p-8 h-full">
            <h3 className="font-heading text-2xl font-bold mb-6">Visit Ainalem</h3>
            <ul className="space-y-5">
              <li className="flex gap-3"><MapPin className="h-5 w-5 text-mustard shrink-0 mt-0.5" /><span>{site.address}</span></li>
              <li className="flex gap-3"><Phone className="h-5 w-5 text-mustard shrink-0 mt-0.5" /><a href={`tel:${site.phone}`} className="hover:text-mustard">{site.phone}</a></li>
              <li className="flex gap-3"><Mail className="h-5 w-5 text-mustard shrink-0 mt-0.5" /><a href={`mailto:${site.email}`} className="hover:text-mustard">{site.email}</a></li>
              <li className="flex gap-3"><Clock className="h-5 w-5 text-mustard shrink-0 mt-0.5" /><span>{site.hours}</span></li>
            </ul>
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="rounded-3xl overflow-hidden shadow-lg h-full min-h-[320px]">
            <iframe title="map" src={site.map_embed || "https://www.google.com/maps?q=Seattle,WA&output=embed"} className="w-full h-full min-h-[320px] border-0" loading="lazy" data-testid="contact-map" />
          </div>
        </Reveal>
      </section>
    </div>
  );
}
